import React, { useState, useEffect } from 'react';
import {
  Modal,
  Steps,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Divider,
  List,
  Radio,
  DatePicker,
  Select,
  Input,
  message,
  Spin,
  Alert,
  Row,
  Col,
  Progress
} from 'antd';
import {
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CarOutlined,
  ToolOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { getTokens } from '../../services/authApi';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Step } = Steps;
const { TextArea } = Input;

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api') + '/admin';

interface Estimate {
  id: number;
  estimate_number: string;
  customer?: {
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
  };
  vehicle?: {
    year: number;
    make: string;
    model: string;
    vin: string;
  };
  line_items: Array<{
    description: string;
    labor_hours: number;
    approval_status: string;
  }>;
  total_amount: number;
}

interface AvailableSlot {
  technician_id: number;
  technician_name: string;
  start_time: string;
  end_time: string;
  score: number;
  current_workload_percent: number;
  recommended: boolean;
  rank: number;
}

interface SmartAppointmentBookingProps {
  visible: boolean;
  estimateId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

export const SmartAppointmentBooking: React.FC<SmartAppointmentBookingProps> = ({
  visible,
  estimateId,
  onClose,
  onSuccess
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Estimate data
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [totalLaborHours, setTotalLaborHours] = useState(0);
  const [requiredDurationMinutes, setRequiredDurationMinutes] = useState(0);

  // Available slots
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);

  // Filters
  const [selectedTechnician, setSelectedTechnician] = useState<number | 'all'>('all');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

  // Booking details
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  useEffect(() => {
    if (visible) {
      loadEstimateDuration();
      loadAvailableSlots();
    } else {
      // Reset when modal closes
      setCurrentStep(0);
      setSelectedSlot(null);
      setNotes('');
      setInternalNotes('');
      setSelectedTechnician('all');
      setSelectedDate(null);
    }
  }, [visible, estimateId]);

  useEffect(() => {
    if (visible && currentStep === 1) {
      loadAvailableSlots();
    }
  }, [selectedTechnician, selectedDate]);

  const getAuthHeaders = () => {
    const tokens = getTokens();
    return tokens?.accessToken ? {
      Authorization: `Bearer ${tokens.accessToken}`
    } : {};
  };

  const loadEstimateDuration = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/appointments/estimate/${estimateId}/duration`,
        { headers: getAuthHeaders() }
      );

      setEstimate(response.data.estimate);
      setTotalLaborHours(response.data.total_labor_hours);
      setRequiredDurationMinutes(response.data.required_duration_minutes);
    } catch (error: any) {
      console.error('Error loading estimate duration:', error);

      // Display the specific error message from the backend
      const errorMessage = error.response?.data?.error || 'Failed to load estimate information';
      const errorCode = error.response?.data?.code;

      if (errorCode === 'NO_APPROVED_LINE_ITEMS') {
        message.error({
          content: errorMessage,
          duration: 8
        });
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async () => {
    setLoading(true);
    try {
      const params: any = {
        max_results: 50
      };

      if (selectedTechnician !== 'all') {
        params.technician_id = selectedTechnician;
      }

      if (selectedDate) {
        params.start_date = selectedDate.startOf('day').toISOString();
        params.end_date = selectedDate.endOf('day').toISOString();
      }

      const response = await axios.get(
        `${API_BASE_URL}/appointments/estimate/${estimateId}/availability`,
        {
          params,
          headers: getAuthHeaders()
        }
      );

      setAvailableSlots(response.data.suggested_slots);

      // Auto-select first recommended slot if none selected
      if (!selectedSlot && response.data.suggested_slots.length > 0) {
        const firstRecommended = response.data.suggested_slots.find((s: AvailableSlot) => s.recommended);
        if (firstRecommended) {
          setSelectedSlot(firstRecommended);
        }
      }
    } catch (error: any) {
      console.error('Error loading available slots:', error);

      // Display the specific error message from the backend
      const errorMessage = error.response?.data?.error || 'Failed to load available time slots';
      const errorCode = error.response?.data?.code;

      if (errorCode === 'NO_APPROVED_LINE_ITEMS') {
        message.error({
          content: errorMessage,
          duration: 8
        });
      } else {
        message.error(errorMessage);
      }

      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAppointment = async () => {
    if (!selectedSlot) {
      message.error('Please select a time slot');
      return;
    }

    const appointmentData = {
      technician_id: selectedSlot.technician_id,
      start_time: selectedSlot.start_time,
      appointment_type: 'repair',
      notes: notes || undefined,
      internal_notes: internalNotes || undefined,
      manual_override: false
    };

    console.log('[BOOKING] Sending appointment data:', appointmentData);
    console.log('[BOOKING] Selected slot:', selectedSlot);

    setSaving(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/appointments/from-estimate/${estimateId}`,
        appointmentData,
        { headers: getAuthHeaders() }
      );

      console.log('[BOOKING] Success response:', response.data);

      // Show success message with details about what was created
      const successMessage = [
        '✅ Appointment created successfully!',
        `📅 Scheduled: ${dayjs(selectedSlot.start_time).format('MMM DD, YYYY @ h:mm A')}`,
        `👨‍🔧 Technician: ${selectedSlot.technician_name}`,
      ];

      if (response.data.work_order) {
        successMessage.push(`📋 Work Order: ${response.data.work_order_number || 'Created'}`);
        if (response.data.assigned_advisor) {
          successMessage.push(`👔 Advisor: ${response.data.assigned_advisor}`);
        }
      }

      message.success({
        content: (
          <div>
            {successMessage.map((msg, i) => (
              <div key={i}>{msg}</div>
            ))}
          </div>
        ),
        duration: 8
      });

      // Close modal and trigger success callback to refresh data
      if (onSuccess) onSuccess();
      onClose();

      // Reset modal state
      setCurrentStep(0);
      setSelectedSlot(null);
      setNotes('');
      setInternalNotes('');
      setSelectedTechnician('all');
      setSelectedDate(null);
      setAvailableSlots([]); // Clear slots to force fresh fetch next time
    } catch (error: any) {
      console.error('[BOOKING] Error creating appointment:', error);
      console.error('[BOOKING] Error response:', error.response?.data);
      console.error('[BOOKING] Error details:', error.response?.data?.details);

      const errorData = error.response?.data;
      const errorMessage = errorData?.error || 'Failed to create appointment';

      // If conflict error, offer to refresh slots
      if (error.response?.status === 409) {
        message.error({
          content: `${errorMessage}. The time slot may no longer be available.`,
          duration: 6
        });
        // Automatically refresh slots after conflict
        setSelectedSlot(null);
        await loadAvailableSlots();
        // Go back to slot selection step
        setCurrentStep(1);
      } else {
        message.error(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return '#52c41a';
    if (score >= 70) return '#1890ff';
    if (score >= 50) return '#faad14';
    return '#ff4d4f';
  };

  const getWorkloadColor = (workload: number) => {
    if (workload < 50) return 'success';
    if (workload < 75) return 'normal';
    if (workload < 90) return 'exception';
    return 'exception';
  };

  // Step 1: Review Estimate
  const renderEstimateReview = () => (
    <div>
      <Alert
        message="Unified Booking System"
        description={
          <div>
            <div style={{ marginBottom: 8 }}>
              This appointment will use the same booking system as the public booking portal at <Text code>/book</Text>
            </div>
            <div style={{ marginBottom: 4 }}>✓ Customer and vehicle information automatically pulled from this estimate</div>
            <div style={{ marginBottom: 4 }}>✓ Work order will be automatically created upon booking</div>
            <div>✓ Advisor will be automatically assigned based on current workload</div>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card title={<><ToolOutlined /> Estimate Summary</>} style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">Estimate Number</Text>
              <br />
              <Text strong>{estimate?.estimate_number}</Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">Customer</Text>
              <br />
              <Text strong>
                {estimate?.customer?.first_name} {estimate?.customer?.last_name}
              </Text>
              <br />
              <Text type="secondary">{estimate?.customer?.phone}</Text>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">Vehicle</Text>
              <br />
              <Text strong>
                <CarOutlined /> {estimate?.vehicle?.year} {estimate?.vehicle?.make} {estimate?.vehicle?.model}
              </Text>
            </div>
            <div>
              <Text type="secondary">Total Amount</Text>
              <br />
              <Text strong style={{ fontSize: '20px', color: '#1890ff' }}>
                ${estimate?.total_amount?.toFixed(2)}
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      <Card title={<><ClockCircleOutlined /> Labor Time Breakdown</>}>
        <Alert
          message={`Total Time Required: ${totalLaborHours.toFixed(1)} hours (${Math.round(requiredDurationMinutes / 60)}h ${requiredDurationMinutes % 60}m)`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <List
          dataSource={estimate?.line_items || []}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                title={item.description}
                description={
                  <Space>
                    <Tag color="blue">{item.labor_hours} hours</Tag>
                    <Tag color="green">{item.approval_status}</Tag>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Card>
    </div>
  );

  // Step 2: Select Time Slot
  const renderTimeSlotSelection = () => (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>Filter Options</Text>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadAvailableSlots()}
              loading={loading}
              size="small"
            >
              Refresh Slots
            </Button>
          </div>
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">Filter by Employee</Text>
                <Select
                  style={{ width: '100%', marginTop: 4 }}
                  value={selectedTechnician}
                  onChange={setSelectedTechnician}
                  options={[
                    { value: 'all', label: 'All Employees (Best Match)' },
                    ...Array.from(new Set(availableSlots.map(s => s.technician_id))).map(id => {
                      const slot = availableSlots.find(s => s.technician_id === id);
                      return {
                        value: id,
                        label: slot?.technician_name || `Employee ${id}`
                      };
                    })
                  ]}
                />
              </Col>
              <Col span={12}>
                <Text type="secondary">Filter by Date</Text>
                <DatePicker
                  style={{ width: '100%', marginTop: 4 }}
                  value={selectedDate}
                  onChange={setSelectedDate}
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Col>
            </Row>
          </div>

          {availableSlots.length > 0 && (
            <Alert
              message={`Found ${availableSlots.length} available time slots`}
              type="success"
              showIcon
            />
          )}

          {availableSlots.length === 0 && !loading && (
            <Alert
              message="No available slots found"
              description="Try adjusting your filters or selecting a different date range"
              type="warning"
              showIcon
            />
          )}
        </Space>
      </Card>

      <Spin spinning={loading}>
        <Radio.Group
          value={selectedSlot ? `${selectedSlot.technician_id}::${selectedSlot.start_time}` : undefined}
          onChange={(e) => {
            const [techId, startTime] = e.target.value.split('::', 2);
            const slot = availableSlots.find(s =>
              s.technician_id === parseInt(techId) && s.start_time === startTime
            );
            setSelectedSlot(slot || null);
          }}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {availableSlots.slice(0, 20).map((slot, index) => (
              <Card
                key={`${slot.technician_id}-${slot.start_time}`}
                size="small"
                hoverable
                style={{
                  borderLeft: slot.recommended ? '4px solid #52c41a' : '1px solid #d9d9d9',
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedSlot(slot)}
              >
                <Radio value={`${slot.technician_id}::${slot.start_time}`} style={{ width: '100%' }}>
                  <Row gutter={16} align="middle">
                    <Col span={8}>
                      <div>
                        <Text strong style={{ fontSize: '16px' }}>
                          {dayjs(slot.start_time).format('MMM DD, YYYY')}
                        </Text>
                        <br />
                        <Text type="secondary">
                          {dayjs(slot.start_time).format('h:mm A')} - {dayjs(slot.end_time).format('h:mm A')}
                        </Text>
                      </div>
                    </Col>

                    <Col span={6}>
                      <div>
                        <UserOutlined /> {slot.technician_name}
                      </div>
                    </Col>

                    <Col span={5}>
                      <div>
                        <Text type="secondary">Workload:</Text>
                        <br />
                        <Progress
                          percent={slot.current_workload_percent}
                          size="small"
                          status={getWorkloadColor(slot.current_workload_percent)}
                          format={percent => `${percent}%`}
                        />
                      </div>
                    </Col>

                    <Col span={5}>
                      <Space>
                        {slot.rank === 1 && <Tag color="gold">Top Choice</Tag>}
                        {slot.recommended && <Tag color="green">Recommended</Tag>}
                        <Tag color={getScoreColor(slot.score)}>
                          Score: {slot.score}
                        </Tag>
                      </Space>
                    </Col>
                  </Row>
                </Radio>
              </Card>
            ))}
          </Space>
        </Radio.Group>
      </Spin>
    </div>
  );

  // Step 3: Confirm & Add Notes
  const renderConfirmation = () => (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Alert
          message="Appointment Ready to Book"
          description="Review the details below and add any notes before confirming"
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
        />
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <Alert
          message="What will be created:"
          description={
            <div>
              <div style={{ marginBottom: 4 }}>📅 <strong>Appointment</strong> - Scheduled with selected technician</div>
              <div style={{ marginBottom: 4 }}>📋 <strong>Work Order</strong> - Automatically created from estimate #{estimate?.estimate_number}</div>
              <div style={{ marginBottom: 4 }}>👔 <strong>Assigned Advisor</strong> - Auto-assigned based on current workload (owners, advisors, and technicians eligible)</div>
              <div>🚗 <strong>Customer & Vehicle</strong> - Information from estimate will be used</div>
            </div>
          }
          type="info"
          showIcon
        />
      </Card>

      {selectedSlot && (
        <Card title="Selected Time Slot" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 12 }}>
                <CalendarOutlined style={{ marginRight: 8 }} />
                <Text strong>{dayjs(selectedSlot.start_time).format('dddd, MMMM DD, YYYY')}</Text>
              </div>
              <div style={{ marginBottom: 12 }}>
                <ClockCircleOutlined style={{ marginRight: 8 }} />
                <Text strong>
                  {dayjs(selectedSlot.start_time).format('h:mm A')} - {dayjs(selectedSlot.end_time).format('h:mm A')}
                </Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  ({Math.round(requiredDurationMinutes / 60)}h {requiredDurationMinutes % 60}m)
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 12 }}>
                <UserOutlined style={{ marginRight: 8 }} />
                <Text strong>{selectedSlot.technician_name}</Text>
              </div>
              <div>
                <Tag color="blue">Current Workload: {selectedSlot.current_workload_percent}%</Tag>
                {selectedSlot.recommended && <Tag color="green">Recommended</Tag>}
              </div>
            </Col>
          </Row>
        </Card>
      )}

      <Card title="Appointment Notes" style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Text strong>Customer Notes (visible to customer)</Text>
          <TextArea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes that the customer should see..."
            style={{ marginTop: 8 }}
          />
        </div>

        <div>
          <Text strong>Internal Notes (for shop use only)</Text>
          <TextArea
            rows={3}
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Add any internal notes for the technician..."
            style={{ marginTop: 8 }}
          />
        </div>
      </Card>
    </div>
  );

  const steps = [
    {
      title: 'Review Estimate',
      icon: <ToolOutlined />,
      content: renderEstimateReview()
    },
    {
      title: 'Select Time',
      icon: <CalendarOutlined />,
      content: renderTimeSlotSelection()
    },
    {
      title: 'Confirm',
      icon: <CheckCircleOutlined />,
      content: renderConfirmation()
    }
  ];

  return (
    <Modal
      title={
        <div>
          <CalendarOutlined /> Smart Appointment Booking
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Space>
            {currentStep > 0 && (
              <Button onClick={() => setCurrentStep(currentStep - 1)}>
                Previous
              </Button>
            )}
            {currentStep < steps.length - 1 && (
              <Button
                type="primary"
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={currentStep === 1 && !selectedSlot}
              >
                Next
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button
                type="primary"
                onClick={handleCreateAppointment}
                loading={saving}
                disabled={!selectedSlot}
              >
                Book Appointment
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        {steps.map((step, index) => (
          <Step key={index} title={step.title} icon={step.icon} />
        ))}
      </Steps>

      <div style={{ minHeight: '400px' }}>
        {steps[currentStep].content}
      </div>
    </Modal>
  );
};
